import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { TeamRosterPanel } from "../components/TeamRosterPanel";

type Section = "roster" | "members" | "invites";

export function TeamDetail() {
  const { teamId = "" } = useParams();
  const teams = useAsync(() => api.listTeams(true), []);
  const members = useAsync(() => api.listMembers(teamId), [teamId]);
  const roster = useAsync(() => api.getTeamRoster(teamId), [teamId]);

  const team = teams.data?.items.find((t) => t.teamId === teamId);
  const isAdmin = team?.role === "admin";
  const isArchived = team?.archived ?? false;

  const [section, setSection] = useState<Section>("roster");

  // Only admins can read join codes (endpoint is admin-gated); skip for members.
  const codes = useAsync(
    () =>
      isAdmin
        ? api.listJoinCodes(teamId)
        : Promise.resolve({ items: [], nextCursor: null }),
    [teamId, isAdmin],
  );

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "parent">("parent");
  const [err, setErr] = useState<string>();

  const addMember = async () => {
    setErr(undefined);
    try {
      await api.addMember(teamId, email.trim(), role);
      setEmail("");
      members.reload();
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const makeCode = async () => {
    await api.createJoinCode(teamId);
    codes.reload();
  };
  const inviteParent = async (parentEmail: string) => {
    setErr(undefined);
    try {
      await api.addMember(teamId, parentEmail.toLowerCase(), "parent");
      members.reload();
      alert(
        `Invited ${parentEmail} as a parent. They'll see team details once they sign in.`,
      );
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  // Summary stats for the header.
  const memberCount = members.data?.items.length ?? 0;
  const adminCount =
    members.data?.items.filter((m) => m.role === "admin").length ?? 0;
  const rosterStats = useMemo(() => {
    const cfg = roster.data?.config;
    const entries = roster.data?.entries ?? [];
    const classes = cfg?.baseWeightClasses.length ?? 0;
    const filled = new Set(entries.map((e) => e.weightClass)).size;
    return { classes, filled, wrestlers: entries.length, level: cfg?.level };
  }, [roster.data]);

  return (
    <div>
      {/* --- Summary header --- */}
      <div className="row spread" style={{ alignItems: "center" }}>
        <div>
          <h1 style={{ marginBottom: "0.1rem" }}>{team?.name ?? "Team"}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {isAdmin ? "You're a Coach" : "You're a Parent"} · {teamId}
          </p>
        </div>
        <div className="row" style={{ gap: "0.4rem" }}>
          {isArchived && <span className="badge cancelled">archived</span>}
          <span className="badge accent">{isAdmin ? "Coach" : "Parent"}</span>
        </div>
      </div>

      <div className="team-summary">
        <div className="team-stat">
          <span className="team-stat-num">{memberCount}</span>
          <span className="muted">
            member{memberCount === 1 ? "" : "s"}
            {adminCount > 0 ? ` · ${adminCount} admin` : ""}
          </span>
        </div>
        <div className="team-stat">
          <span className="team-stat-num">{rosterStats.wrestlers}</span>
          <span className="muted">
            roster wrestler{rosterStats.wrestlers === 1 ? "" : "s"}
          </span>
        </div>
        <div className="team-stat">
          <span className="team-stat-num">
            {rosterStats.classes
              ? `${rosterStats.filled}/${rosterStats.classes}`
              : "—"}
          </span>
          <span className="muted">classes filled</span>
        </div>
        <Link to={`/standings`} className="team-stat team-stat-link">
          <span className="team-stat-num">↗</span>
          <span className="muted">Standings</span>
        </Link>
      </div>

      {isArchived && (
        <div className="card">
          <p className="muted">
            This team is archived — it's hidden from normal lists and locked for
            new meets.{" "}
            {isAdmin && "Unarchive it from the Teams page to make changes."}
          </p>
        </div>
      )}

      {/* --- Section tabs --- */}
      <div className="seg" role="tablist" style={{ margin: "0.75rem 0" }}>
        <button
          role="tab"
          aria-selected={section === "roster"}
          className={section === "roster" ? "seg-on" : ""}
          onClick={() => setSection("roster")}
        >
          Roster
        </button>
        <button
          role="tab"
          aria-selected={section === "members"}
          className={section === "members" ? "seg-on" : ""}
          onClick={() => setSection("members")}
        >
          Members
        </button>
        {isAdmin && (
          <button
            role="tab"
            aria-selected={section === "invites"}
            className={section === "invites" ? "seg-on" : ""}
            onClick={() => setSection("invites")}
          >
            Invites
          </button>
        )}
      </div>

      {err && <p className="error">{err}</p>}

      {/* --- Roster section --- */}
      {section === "roster" && (
        <>
          <TeamRosterPanel
            teamId={teamId}
            isAdmin={isAdmin}
            onInviteParent={inviteParent}
          />
          <TeamCalendarCard teamId={teamId} isAdmin={isAdmin} />
        </>
      )}

      {/* --- Members section --- */}
      {section === "members" && (
        <div className="card">
          <h2>Members</h2>
          {members.loading && <p>Loading…</p>}
          {members.error && <p className="error">{members.error}</p>}
          {members.data?.items.map((m) => (
            <div key={m.userId} className="row spread">
              <span>{m.displayName ?? m.userId}</span>
              <span className="badge">{m.role === "admin" ? "Coach" : "Parent"}</span>
            </div>
          ))}
          {isAdmin && (
            <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border)" }}>
              <h3 style={{ marginTop: 0 }}>Add member</h3>
              <div className="row">
                <input
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <select
                  style={{ width: "auto" }}
                  value={role}
                  onChange={(e) => setRole(e.target.value as "admin" | "parent")}
                >
                  <option value="parent">Parent</option>
                  <option value="admin">Coach</option>
                </select>
                <button onClick={addMember}>Add</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- Invites / join codes section (admin) --- */}
      {section === "invites" && isAdmin && (
        <div className="card">
          <div className="row spread">
            <h2 style={{ margin: 0 }}>Join codes</h2>
            <button onClick={makeCode}>Generate code</button>
          </div>
          <p className="muted">
            Share a join code with parents, or invite them by email from the
            Members tab or straight from a roster entry.
          </p>
          {codes.loading && <p>Loading…</p>}
          {codes.data?.items.map((c) => (
            <div key={c.code} className="row spread">
              <strong>{c.code}</strong>
              <span className="muted">
                {c.expiresAt
                  ? `expires ${c.expiresAt.slice(0, 10)}`
                  : "no expiry"}
              </span>
            </div>
          ))}
          {codes.data && codes.data.items.length === 0 && (
            <p className="muted">No active codes.</p>
          )}
        </div>
      )}
    </div>
  );
}

function TeamCalendarCard({
  teamId,
  isAdmin,
}: {
  teamId: string;
  isAdmin: boolean;
}) {
  const [link, setLink] = useState<{
    httpsUrl: string | null;
    webcalUrl: string | null;
  }>();
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Members auto-load the (read-only) link; admins can also mint it.
  useEffect(() => {
    let cancelled = false;
    api
      .getTeamCalendarLink(teamId)
      .then((res) => {
        if (!cancelled)
          setLink({ httpsUrl: res.httpsUrl, webcalUrl: res.webcalUrl });
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  // Admin mint (when none exists yet).
  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.getTeamCalendarLink(teamId);
      setLink({ httpsUrl: res.httpsUrl, webcalUrl: res.webcalUrl });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link?.httpsUrl) return;
    try {
      await navigator.clipboard.writeText(link.httpsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked; link is visible to copy manually */
    }
  };

  const hasLink = !!link?.httpsUrl;

  return (
    <div className="card">
      <h2>Shared team calendar</h2>
      <p className="muted" style={{ fontSize: "0.85rem" }}>
        Subscribe once and the whole season's meets appear automatically in
        Google / Apple / Outlook — no per-meet setup or Google linking needed.
      </p>
      {!loaded && <p className="muted">Loading…</p>}
      {loaded && hasLink && (
        <>
          <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
            <a href={link!.webcalUrl!}>
              <button>Subscribe (Apple / Google)</button>
            </a>
            <button className="secondary" onClick={copy}>
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>
          <pre className="code-block" style={{ marginTop: "0.5rem" }}>
            {link!.httpsUrl}
          </pre>
        </>
      )}
      {loaded && !hasLink && isAdmin && (
        <button onClick={generate} disabled={busy}>
          {busy ? "Creating…" : "Create calendar link"}
        </button>
      )}
      {loaded && !hasLink && !isAdmin && (
        <p className="muted">
          Your team admin hasn't published the calendar yet.
        </p>
      )}
    </div>
  );
}
